import { DEFAULT_PRESERVE_STATE } from "./const";
import { FrameConfig, PreserveStateToken, RefreshTarget, RemoteFrameRefreshPayload } from "./types";

/**
 * Splits comma-delimited input into trimmed non-empty tokens.
 *
 * @param value - Raw comma-delimited value.
 * @returns Parsed token list.
 */
export function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Parses a numeric attribute value with fallback support.
 *
 * @param value - Raw numeric string value.
 * @param fallback - Fallback when value is missing or invalid.
 * @returns Parsed numeric value.
 */
export function toNumber(value: string | null, fallback: number): number {
  if (value === null) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Parses boolean-ish attribute values (`"", true, 1, yes`).
 *
 * @param value - Raw boolean-like string value.
 * @returns Parsed boolean result.
 */
export function parseBoolean(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return normalized === "" || normalized === "true" || normalized === "1" || normalized === "yes";
}

/**
 * Normalizes request credentials string to a valid `RequestCredentials` value.
 *
 * @param value - Raw credentials attribute value.
 * @returns Normalized credentials mode.
 */
export function parseCredentials(value: string | null): RequestCredentials {
  if (!value) return "same-origin";
  const normalized = value.trim().toLowerCase();
  if (normalized === "include" || normalized === "omit") return normalized;
  return "same-origin";
}

/**
 * Parses preserve-state tokens into a validated fallback-safe set.
 *
 * @param raw - Raw preserve-state value.
 * @returns Valid preserve-state token set.
 */
export function parsePreserveState(raw: string | null): Set<PreserveStateToken> {
  const value = raw ?? DEFAULT_PRESERVE_STATE;
  const out = new Set<PreserveStateToken>();

  const parts = splitCsv(value);
  for (let i = 0; i < parts.length; i++) {
    const token = parts[i] as PreserveStateToken;
    if (token === "focus" || token === "value" || token === "selection" || token === "scroll") {
      out.add(token);
    }
  }

  if (out.size === 0) {
    out.add("focus");
    out.add("value");
    out.add("selection");
    out.add("scroll");
  }

  return out;
}

/**
 * Checks whether an element has any `data-frame-*` attributes.
 *
 * @param element - Candidate element.
 * @returns `true` when at least one matching attribute exists.
 */
export function hasFrameDatasetAttributes(element: Element): boolean {
  for (let i = 0; i < element.attributes.length; i++) {
    if (element.attributes[i].name.startsWith("data-frame-")) {
      return true;
    }
  }
  return false;
}

/**
 * Performs a safe selector match check.
 *
 * @param element - Element to test.
 * @param selector - Selector to evaluate.
 * @returns `true` when selector matches.
 */
export function elementMatchesSelector(element: Element, selector: string): boolean {
  try {
    return element.matches(selector);
  } catch {
    return false;
  }
}

/**
 * Reads the most relevant event value for `__value__` query substitutions.
 *
 * @param event - Trigger event.
 * @param target - Trigger target element.
 * @returns Resolved string value.
 */
export function readEventValue(event: Event, target: Element): string {
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
    return event.target.value;
  }

  if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) {
    return target.value;
  }

  return target.getAttribute("value") ?? "";
}

/**
 * Coerces arbitrary input into a refresh payload object.
 *
 * @param payload - Unknown payload input.
 * @returns Normalized payload object.
 */
export function normalizeRefreshPayload(payload: unknown): RemoteFrameRefreshPayload {
  if (!payload || typeof payload !== "object") {
    return {};
  }
  return payload as RemoteFrameRefreshPayload;
}

/**
 * Checks whether an element has overflowable content on any axis.
 *
 * @param element - Candidate element.
 * @returns `true` when element is scrollable.
 */
export function isElementScrollable(element: HTMLElement): boolean {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}

/**
 * Escapes an identifier for safe usage in CSS selectors.
 *
 * @param value - Identifier value.
 * @returns Escaped selector-safe identifier.
 */
export function escapeCssIdentifier(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }

  return value.replace(/([ #;?%&,.+*~':"!^$[\]()=>|/@])/g, "\\$1");
}

/**
 * Builds a stable signature for scheduler options that affect patch behavior.
 *
 * @param config - Active resolved frame config.
 * @returns Deterministic scheduler signature string.
 */
export function createSchedulerSignature(config: FrameConfig): string {
  const preserveValueOrSelection =
    config.preserveState.has("value") || config.preserveState.has("selection");
  const restoreFocus = config.preserveState.has("focus") || config.preserveState.has("selection");

  return `${config.executeScripts}::${preserveValueOrSelection ? "1" : "0"}::${restoreFocus ? "1" : "0"}`;
}

/**
 * Type guard for AbortError-like failures.
 *
 * @param error - Unknown error candidate.
 * @returns `true` when error shape indicates abort semantics.
 */
export function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const maybe = error as { name?: string };
  return maybe.name === "AbortError";
}

/**
 * Creates an AbortError-compatible error object across environments.
 *
 * @param message - Abort reason message.
 * @returns AbortError-like error object.
 */
export function createAbortError(message: string): Error {
  if (typeof DOMException !== "undefined") {
    return new DOMException(message, "AbortError");
  }
  const error = new Error(message);
  error.name = "AbortError";
  return error;
}

/**
 * Resolves on the next animation frame.
 *
 * @returns Promise resolved on next frame.
 */
export function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

/**
 * Matches refresh target addressing against the current frame and event origin.
 *
 * @param frame - Remote-frame host element.
 * @param target - Target addressing directive.
 * @param event - Trigger event.
 * @returns `true` when the frame should handle the refresh.
 */
export function matchesRefreshTarget(
  frame: HTMLElement,
  target: RefreshTarget,
  event: Event
): boolean {
  if (target === "self") {
    return event.target === frame;
  }

  if (target === "closest") {
    if (!(event.target instanceof Element)) {
      return event.target === frame;
    }
    return event.target.closest("remote-frame") === frame;
  }

  if (target.startsWith("id:")) {
    const id = target.slice(3);
    return Boolean(id) && frame.id === id;
  }

  if (target.startsWith("selector:")) {
    const selector = target.slice(9).trim();
    if (!selector) return false;
    try {
      return frame.matches(selector);
    } catch {
      return false;
    }
  }

  return false;
}
